import { DEFAULT_LORE_HTML } from '../constants.js';
import { escapeHtml } from '../helpers.js';

/**
 * Attach info-panel behavior (tabs, resizing, state) to MapApp.
 * @param {typeof import('../app.js').default} App
 */
export function applyPanelMixins(App) {
	/**
	 * Switch between lore and search tabs, updating focus and content.
	 * @param {string} panelName - Target tab name ('lore' or 'search').
	 */
	App.prototype.activatePanel = function activatePanel(panelName) {
		const target = panelName === 'search' ? 'search' : 'lore';
		if (this.activePanel === target) {
			if (target === 'search') {
				this.renderSearchResults(this.searchInputEl ? this.searchInputEl.value : '');
			}
			return;
		}
		this.activePanel = target;
		for (let i = 0; i < this.tabButtons.length; i += 1) {
			const button = this.tabButtons[i];
			const panel = button.getAttribute('data-panel');
			const isActive = panel === target;
			button.classList.toggle('is-active', isActive);
			button.setAttribute('aria-selected', isActive);
		}
		if (this.lorePanelEl) {
			this.lorePanelEl.hidden = target !== 'lore';
		}
		if (this.searchPanelEl) {
			this.searchPanelEl.hidden = target !== 'search';
			if (target === 'search') {
				this.renderSearchResults(this.searchInputEl ? this.searchInputEl.value : '');
				this.updateSearchSelection();
				if (this.searchInputEl) {
					setTimeout(() => {
						this.searchInputEl.focus();
					}, 0);
				}
			}
		}
	};

	/**
	 * Collapse the side panel, updating ARIA state and resizing the map.
	 */
	App.prototype.collapsePanel = function collapsePanel() {
		if (!this.infoPanel && !this.infoPanelShell) {
			return;
		}
		if (this.infoPanelShell) {
			this.infoPanelShell.setAttribute('data-state', 'collapsed');
		}
		if (this.infoPanel) {
			this.infoPanel.setAttribute('data-state', 'collapsed');
		}
		if (this.infoToggleButton) {
			this.infoToggleButton.setAttribute('aria-expanded', 'false');
		}
		this.scheduleMapResize();
	};

	/**
	 * Expand the side panel if it is currently collapsed.
	 * @param {boolean} [force=false] - Skip state check to force expansion.
	 */
	App.prototype.expandPanel = function expandPanel(force) {
		if (!this.infoPanel && !this.infoPanelShell) {
			return;
		}
		const current = this.getPanelState();
		if (!force && current === 'expanded') {
			return;
		}
		if (this.infoPanelShell) {
			this.infoPanelShell.setAttribute('data-state', 'expanded');
		}
		if (this.infoPanel) {
			this.infoPanel.setAttribute('data-state', 'expanded');
		}
		if (this.infoToggleButton) {
			this.infoToggleButton.setAttribute('aria-expanded', 'true');
		}
		this.scheduleMapResize();
	};

	/**
	 * Determine the current panel state ('expanded' or 'collapsed').
	 * @returns {string}
	 */
	App.prototype.getPanelState = function getPanelState() {
		if (this.infoPanelShell && this.infoPanelShell.hasAttribute('data-state')) {
			return this.infoPanelShell.getAttribute('data-state') || 'expanded';
		}
		if (this.infoPanel && this.infoPanel.hasAttribute('data-state')) {
			return this.infoPanel.getAttribute('data-state') || 'expanded';
		}
		return 'expanded';
	};

	/**
	 * Invalidate Leaflet sizing twice to keep the map in sync with panel width.
	 */
	App.prototype.scheduleMapResize = function scheduleMapResize() {
		if (!this.map) {
			return;
		}
		this.map.invalidateSize();
		setTimeout(() => {
			if (this.map) {
				this.map.invalidateSize();
			}
		}, this.mapResizeDelay);
	};

	/**
	 * Wire up pointer handlers for resizing the panel on desktop layouts.
	 */
	App.prototype.initializePanelResize = function initializePanelResize() {
		if (!this.infoPanelShell || this.panelResizeInitialized) {
			return;
		}
		this.panelResizeInitialized = true;
		this.panelResizeHandle =
			this.infoPanelShell.querySelector('.info-panel__resize-handle') || null;
		if (!this.panelResizeHandle) {
			return;
		}
		this.boundPanelResizePointerMove = (event) =>
			this.onPanelResizePointerMove(event);
		this.boundPanelResizePointerUp = (event) => this.onPanelResizePointerUp(event);
		this.boundPanelResizeWindowChange = () => this.syncPanelWidthWithinBounds();
		this.panelResizeHandle.addEventListener('pointerdown', (event) =>
			this.onPanelResizePointerDown(event)
		);
		if (
			globalThis.window &&
			typeof globalThis.window.addEventListener === 'function'
		) {
			globalThis.window.addEventListener('resize', this.boundPanelResizeWindowChange);
		}
	};

	/**
	 * Determine whether manual panel resizing is allowed for the current state.
	 * @returns {boolean}
	 */
	App.prototype.canResizePanel = function canResizePanel() {
		if (!this.panelResizeHandle || !this.infoPanelShell) {
			return false;
		}
		if (this.getPanelState() === 'collapsed') {
			return false;
		}
		if (
			globalThis.window &&
			typeof globalThis.window.matchMedia === 'function'
		) {
			try {
				if (globalThis.window.matchMedia('(max-width: 900px)').matches) {
					return false;
				}
			} catch (error) {
				console.warn(error);
			}
		}
		return true;
	};

	/**
	 * Begin a resize drag operation from the resize handle.
	 * @param {PointerEvent} event
	 */
	App.prototype.onPanelResizePointerDown = function onPanelResizePointerDown(event) {
		if (!this.canResizePanel()) {
			return;
		}
		event.preventDefault();
		this.panelResizeActive = true;
		this.panelResizePointerId = event.pointerId;
		this.panelResizeStartX = event.clientX;
		this.panelResizeStartWidth = this.getInfoPanelWidthPx();
		if (
			this.panelResizeHandle &&
			typeof this.panelResizeHandle.setPointerCapture === 'function'
		) {
			try {
				this.panelResizeHandle.setPointerCapture(event.pointerId);
			} catch (error) {
				console.warn(error);
			}
		}
		const doc = globalThis.document;
		if (doc) {
			doc.addEventListener('pointermove', this.boundPanelResizePointerMove);
			doc.addEventListener('pointerup', this.boundPanelResizePointerUp);
			doc.addEventListener('pointercancel', this.boundPanelResizePointerUp);
		}
		if (this.infoPanelShell) {
			this.infoPanelShell.classList.add('is-resizing');
		}
	};

	/**
	 * Handle pointer movement while resizing to update width live.
	 * @param {PointerEvent} event
	 */
	App.prototype.onPanelResizePointerMove = function onPanelResizePointerMove(event) {
		if (!this.panelResizeActive) {
			return;
		}
		event.preventDefault();
		const delta = this.panelResizeStartX - event.clientX;
		const proposedWidth = this.panelResizeStartWidth + delta;
		const bounds = this.getPanelWidthBounds();
		const clamped =
			typeof proposedWidth === 'number'
				? Math.min(Math.max(proposedWidth, bounds.min), bounds.max)
				: bounds.min;
		this.setPanelWidth(clamped);
		this.queuePanelResizeInvalidate();
	};

	/**
	 * Finish a resize drag, removing pointer listeners and cleaning state.
	 * @param {PointerEvent} event
	 */
	App.prototype.onPanelResizePointerUp = function onPanelResizePointerUp(event) {
		if (!this.panelResizeActive) {
			return;
		}
		event.preventDefault();
		this.panelResizeActive = false;
		const doc = globalThis.document;
		if (doc) {
			doc.removeEventListener('pointermove', this.boundPanelResizePointerMove);
			doc.removeEventListener('pointerup', this.boundPanelResizePointerUp);
			doc.removeEventListener('pointercancel', this.boundPanelResizePointerUp);
		}
		if (
			this.panelResizeHandle &&
			typeof this.panelResizeHandle.releasePointerCapture === 'function'
		) {
			try {
				this.panelResizeHandle.releasePointerCapture(this.panelResizePointerId);
			} catch (error) {
				console.warn(error);
			}
		}
		this.panelResizePointerId = null;
		if (this.infoPanelShell) {
			this.infoPanelShell.classList.remove('is-resizing');
		}
		this.cancelPanelResizeInvalidate();
		this.scheduleMapResize();
	};

/**
 * Debounce map invalidation while the resize handle is dragged.
 */
App.prototype.queuePanelResizeInvalidate = function queuePanelResizeInvalidate() {
		if (!this.map || this.panelResizeInvalidationScheduled) {
			return;
		}
		this.panelResizeInvalidationScheduled = true;
		const trigger = () => {
			this.panelResizeInvalidationScheduled = false;
			this.panelResizeRafId = null;
			if (this.map) {
				this.map.invalidateSize();
			}
		};
		if (typeof globalThis.requestAnimationFrame === 'function') {
			this.panelResizeRafId = globalThis.requestAnimationFrame(trigger);
			return;
		}
		this.panelResizeRafId = setTimeout(trigger, 16);
	};

/**
 * Cancel any pending resize invalidation task.
 */
App.prototype.cancelPanelResizeInvalidate = function cancelPanelResizeInvalidate() {
		if (!this.panelResizeRafId) {
			return;
		}
		if (typeof globalThis.cancelAnimationFrame === 'function') {
			globalThis.cancelAnimationFrame(this.panelResizeRafId);
		} else {
			clearTimeout(this.panelResizeRafId);
		}
		this.panelResizeRafId = null;
		this.panelResizeInvalidationScheduled = false;
	};

/**
 * Persist and apply the panel width CSS custom property.
 * @param {number} widthPx
 */
App.prototype.setPanelWidth = function setPanelWidth(widthPx) {
		if (!this.infoPanelShell || typeof widthPx !== 'number' || widthPx <= 0) {
			return;
		}
		const rounded = Math.round(widthPx);
		this.panelWidthSetting = rounded;
		this.infoPanelShell.style.setProperty('--info-panel-width', `${rounded}px`);
	};

/**
 * Measure current info panel width.
 * @returns {number}
 */
App.prototype.getInfoPanelWidthPx = function getInfoPanelWidthPx() {
		if (!this.infoPanelShell) {
			return 0;
		}
		const rect = this.infoPanelShell.getBoundingClientRect();
		return rect.width || 0;
	};

/**
 * Compute min/max width constraints from CSS custom props.
 * @returns {{min:number,max:number}}
 */
App.prototype.getPanelWidthBounds = function getPanelWidthBounds() {
		const defaults = { min: 280, max: 544 };
		if (!this.infoPanelShell) {
			return defaults;
		}
		const parentWidth = this.infoPanelShell.parentElement
			? this.infoPanelShell.parentElement.clientWidth
			: 0;
		const styles =
			globalThis.window && typeof globalThis.window.getComputedStyle === 'function'
				? globalThis.window.getComputedStyle(this.infoPanelShell)
				: null;
		const min = this.parsePanelSizeValue(
			styles ? styles.getPropertyValue('--info-panel-min-width') : '',
			defaults.min,
			parentWidth
		);
		const max = this.parsePanelSizeValue(
			styles ? styles.getPropertyValue('--info-panel-max-width') : '',
			defaults.max,
			parentWidth
		);
		const bounds = {
			min,
			max: Math.max(min, max)
		};
		if (parentWidth > 0) {
			const reservedForMap = 320;
			const halfWidth = parentWidth * 0.5;
			const allowableMax = Math.max(
				bounds.min,
				Math.min(parentWidth - reservedForMap, halfWidth)
			);
			bounds.max = Math.min(bounds.max, allowableMax);
		}
		return bounds;
	};

/**
 * Parse CSS length values (px or %) into pixel counts.
 * @param {string} value
 * @param {number} fallback
 * @param {number} parentWidth
 * @returns {number}
 */
App.prototype.parsePanelSizeValue = function parsePanelSizeValue(
	value,
	fallback,
	parentWidth
) {
		if (!value) {
			return fallback;
		}
		const trimmed = String(value).trim();
		if (!trimmed) {
			return fallback;
		}
		const numeric = parseFloat(trimmed);
		if (Number.isNaN(numeric)) {
			return fallback;
		}
		const normalizedValue = trimmed.toLowerCase();
		if (normalizedValue.endsWith('rem')) {
			const baseSize =
				globalThis.window &&
				globalThis.document &&
				typeof globalThis.window.getComputedStyle === 'function'
					? parseFloat(
							globalThis.window.getComputedStyle(
								globalThis.document.documentElement
							).fontSize || '16'
					  )
					: 16;
			const normalized = Number.isNaN(baseSize) ? 16 : baseSize;
			return numeric * normalized;
		}
		if (normalizedValue.endsWith('vw')) {
			const viewportWidth =
				globalThis.window && typeof globalThis.window.innerWidth === 'number'
					? globalThis.window.innerWidth
					: 0;
			if (viewportWidth > 0) {
				return (numeric / 100) * viewportWidth;
			}
			return fallback;
		}
		if (normalizedValue.endsWith('%')) {
			if (typeof parentWidth === 'number' && parentWidth > 0) {
				return (numeric / 100) * parentWidth;
			}
			return fallback;
		}
		return numeric;
	};

/**
 * Clamp stored width when viewport changes so the panel stays valid.
 */
App.prototype.syncPanelWidthWithinBounds = function syncPanelWidthWithinBounds() {
		if (!this.infoPanelShell || typeof this.panelWidthSetting !== 'number') {
			return;
		}
		const bounds = this.getPanelWidthBounds();
		const clamped = Math.min(
			Math.max(this.panelWidthSetting, bounds.min),
			bounds.max
		);
		if (clamped !== this.panelWidthSetting) {
			this.setPanelWidth(clamped);
			this.scheduleMapResize();
		}
	};

/**
 * Render lore HTML along with a title heading.
 * @param {string} title
 * @param {string} html
 */
App.prototype.setPanelContent = function setPanelContent(title, html) {
		if (!this.lorePanelEl) {
			return;
		}
		this.lorePanelEl.innerHTML =
			'<h1 class="info-panel__title">' +
			escapeHtml(title) +
			'</h1>' +
			'<div class="info-panel__content">' +
			html +
			'</div>';
		this.lorePanelEl.scrollTop = 0;
	};

/**
 * Display a simple message (no wrapper content div).
 * @param {string} title
 * @param {string} messageHtml
 */
App.prototype.setPanelMessage = function setPanelMessage(title, messageHtml) {
		if (!this.lorePanelEl) {
			return;
		}
		this.lorePanelEl.innerHTML =
			'<h1 class="info-panel__title">' +
			escapeHtml(title) +
			'</h1>' +
			messageHtml;
		this.lorePanelEl.scrollTop = 0;
	};

/**
 * Show the default project lore splash content.
 */
App.prototype.showPlaceholder = function showPlaceholder() {
		if (!this.lorePanelEl) {
			return;
		}
		this.expandPanel(true);
		this.clearHighlight();
		this.lorePanelEl.innerHTML = DEFAULT_LORE_HTML;
	};

/**
 * Toggle collapse/expand when the chevron button is clicked.
 * @param {MouseEvent} event
 */
App.prototype.onToggleClick = function onToggleClick(event) {
		event.preventDefault();
		if (!this.infoPanel && !this.infoPanelShell) {
			return;
		}

		const current = this.getPanelState();
		if (current === 'expanded') {
			this.collapsePanel();
		} else {
			this.expandPanel();
		}
	};

/**
 * Delegate click events inside the panel for copy or internal links.
 * @param {MouseEvent} event
 */
App.prototype.onInfoPanelClick = function onInfoPanelClick(event) {
		const actionTarget = event.target.closest('[data-action="copy-json"]');
		if (actionTarget) {
			event.preventDefault();
			this.copyJsonFromPanel(actionTarget);
			return;
		}

		const anchor = event.target.closest('a');
		if (!anchor) {
			return;
		}

		const href = anchor.getAttribute('href');
		if (!href) {
			return;
		}

		const link = this.resolveContentLink(href);
		if (!link) {
			return;
		}

		event.preventDefault();
		this.handleContentLink(link);
	};

/**
 * Copy JSON payloads within the debugging panel.
 * @param {HTMLElement} trigger
 */
App.prototype.copyJsonFromPanel = function copyJsonFromPanel(trigger) {
		if (!trigger || !this.lorePanelEl) {
			return;
		}
		if (
			trigger.hasAttribute('disabled') ||
			trigger.getAttribute('data-json-ready') === 'false'
		) {
			return;
		}
		const sourceKey = trigger.getAttribute('data-json-source') || 'primary';
		const payloadEl = this.findJsonPayloadElement(sourceKey);
		if (!payloadEl) {
			return;
		}
		const text = payloadEl.textContent || '';
		if (!text.trim()) {
			return;
		}
		const cacheOriginalLabel = () => {
			if (!trigger.hasAttribute('data-original-label')) {
				trigger.setAttribute('data-original-label', trigger.textContent || 'Copy JSON');
			}
		};
		const resetLabel = () => {
			const original = trigger.getAttribute('data-original-label');
			if (original) {
				trigger.textContent = original;
			}
			trigger.classList.remove('is-copied');
			trigger.removeAttribute('data-copy-error');
		};
		const blurTrigger = () => {
			if (typeof trigger.blur === 'function') {
				trigger.blur();
			}
		};
		const markCopied = () => {
			cacheOriginalLabel();
			trigger.classList.add('is-copied');
			trigger.textContent = 'Copied!';
			blurTrigger();
			setTimeout(() => {
				resetLabel();
			}, 1600);
		};
		const markFailed = () => {
			cacheOriginalLabel();
			trigger.setAttribute('data-copy-error', 'true');
			trigger.textContent = 'Copy failed';
			blurTrigger();
			setTimeout(() => {
				resetLabel();
			}, 2000);
		};
		const fallbackCopy = () => {
			if (typeof document === 'undefined') {
				markFailed();
				return;
			}
			try {
				const textarea = document.createElement('textarea');
				textarea.value = text;
				textarea.setAttribute('readonly', 'true');
				textarea.style.position = 'fixed';
				textarea.style.top = '-2000px';
				document.body.appendChild(textarea);
				textarea.focus();
				textarea.select();
				const success = document.execCommand('copy');
				document.body.removeChild(textarea);
				if (success) {
					markCopied();
				} else {
					markFailed();
				}
			} catch (error) {
				console.warn('Clipboard copy failed', error);
				markFailed();
			}
		};
		if (
			globalThis.navigator &&
			globalThis.navigator.clipboard &&
			typeof globalThis.navigator.clipboard.writeText === 'function'
		) {
			globalThis.navigator.clipboard.writeText(text).then(markCopied).catch(() => {
				fallbackCopy();
			});
			return;
		}
		fallbackCopy();
	};

	App.prototype.findJsonPayloadElement = function findJsonPayloadElement(sourceKey) {
		if (!this.lorePanelEl || !sourceKey) {
			return null;
		}
		const normalized = String(sourceKey).replace(/[^a-z0-9_-]/gi, '');
		if (!normalized) {
			return null;
		}
		return (
			this.lorePanelEl.querySelector(`[data-json-payload="${normalized}"]`) || null
		);
	};

	App.prototype.setJsonCopyReadyState = function setJsonCopyReadyState(sourceKey, isReady) {
		if (!this.lorePanelEl || !sourceKey) {
			return;
		}
		const normalized = String(sourceKey).replace(/[^a-z0-9_-]/gi, '');
		if (!normalized) {
			return;
		}
		const button = this.lorePanelEl.querySelector(
			`.json-copy-button[data-json-source="${normalized}"]`
		);
		if (!button) {
			return;
		}
		if (isReady) {
			button.removeAttribute('disabled');
			button.removeAttribute('aria-disabled');
			button.setAttribute('data-json-ready', 'true');
			return;
		}
		button.setAttribute('disabled', 'true');
		button.setAttribute('aria-disabled', 'true');
		button.setAttribute('data-json-ready', 'false');
	};

	App.prototype.schedulePanelReveal = function schedulePanelReveal(delayMs) {
		if (this.panelRevealScheduled) {
			return;
		}
		this.panelRevealScheduled = true;

		const reveal = () => {
			if (!this.lastAnchorValue) {
				this.showPlaceholder();
			}
			this.animatePanelReveal();
		};

		const revealDelay =
			typeof delayMs === 'number' && delayMs >= 0 ? delayMs : 360;
		const scheduler =
			typeof globalThis.requestIdleCallback === 'function'
				? globalThis.requestIdleCallback
				: (cb) => {
						return setTimeout(cb, 320);
					};

		scheduler(() => {
			setTimeout(reveal, revealDelay);
		});
	};

	App.prototype.animatePanelReveal = function animatePanelReveal() {
		if (!this.infoPanel || this.prefersReducedMotion()) {
			return;
		}
		this.infoPanel.classList.remove('is-sliding');
		void this.infoPanel.offsetWidth;
		this.infoPanel.classList.add('is-sliding');
		setTimeout(() => {
			if (this.infoPanel) {
				this.infoPanel.classList.remove('is-sliding');
			}
		}, 720);
	};

	App.prototype.prefersReducedMotion = function prefersReducedMotion() {
		if (
			typeof globalThis.window === 'undefined' ||
			typeof globalThis.window.matchMedia !== 'function'
		) {
			return false;
		}
		try {
			return globalThis.window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		} catch (error) {
			console.warn(error);
			return false;
		}
	};
}
